/** Small presentational form controls shared across the app. */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { SCALES, scaleFactor, splitCompact, type ScaleKey } from '../model';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  title?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
}) {
  return (
    <div className="segmented" role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.title}
          className={option.value === value ? 'active' : undefined}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function ScaleToggle({
  value,
  onChange,
}: {
  value: 'linear' | 'log';
  onChange: (value: 'linear' | 'log') => void;
}) {
  return (
    <SegmentedControl
      value={value}
      onChange={onChange}
      options={[
        { value: 'linear', label: 'Lin', title: 'Linear y axis' },
        { value: 'log', label: 'Log', title: 'Logarithmic y axis' },
      ]}
    />
  );
}

export function Field({
  label,
  children,
  inline,
}: {
  label: string;
  children: ReactNode;
  /** Label beside the control rather than above it. */
  inline?: boolean;
}) {
  return (
    <label className={`field${inline ? ' field-inline' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

export function NumberField({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  suffix,
  wide,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  /** For values in the millions, which do not fit the default width. */
  wide?: boolean;
  /** Accessible name, for inputs not wrapped in a <Field>. */
  label?: string;
}) {
  return (
    <span className={`number-field${wide ? ' wide' : ''}`}>
      <input
        type="number"
        aria-label={label}
        value={Number.isFinite(value) ? value : 0}
        min={min}
        max={max}
        step={step}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (!Number.isFinite(next)) return;
          onChange(Math.min(Math.max(next, min), max ?? Number.MAX_SAFE_INTEGER));
        }}
      />
      {suffix ? <span className="suffix">{suffix}</span> : null}
    </span>
  );
}

interface Draft {
  mantissa: string;
  scale: ScaleKey;
}

/**
 * A number that reads as text until you click it, then becomes an input.
 *
 * This is what makes the selection pane itself the range editor: every figure
 * it prints is also the control that sets it. Commit on Enter or on leaving the
 * field, abandon on Escape.
 *
 * The value and its scale suffix are **separate controls**: the input holds
 * "3.2" and a dropdown holds "M". Selecting the input and typing therefore
 * cannot silently wipe the magnitude, which is the whole reason they are split.
 * Since printing at one decimal is lossy, an untouched field is a deliberate
 * no-op — only a real edit moves anything.
 */
export function EditableNumber({
  display,
  onCommit,
  label,
  min = 0,
  max,
  scalable = true,
  suffix,
}: {
  /** Formatted text — shown while idle, and seeds the editor when clicked. */
  display: string;
  /** Called with the parsed value, in this field's own unit. */
  onCommit: (value: number) => void;
  /** Accessible name; also used for the hover hint. */
  label: string;
  min?: number;
  max?: number;
  /** False for quantities that never reach thousands, e.g. a percentage. */
  scalable?: boolean;
  /** Static trailing unit shown while editing, e.g. "%". */
  suffix?: string;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const seed = useRef<Draft>({ mantissa: '', scale: '' });
  const editing = draft !== null;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        className="editable"
        aria-label={label}
        title={`Click to set ${label.toLowerCase()}`}
        onClick={() => {
          const parts = splitCompact(display) ?? { mantissa: 0, scale: '' as ScaleKey };
          const next: Draft = { mantissa: String(parts.mantissa), scale: parts.scale };
          seed.current = next;
          setDraft(next);
        }}
      >
        {display}
      </button>
    );
  }

  const commit = () => {
    const current = draft;
    setDraft(null);
    if (
      current.mantissa.trim() === seed.current.mantissa.trim() &&
      current.scale === seed.current.scale
    ) {
      return;
    }
    const mantissa = Number(current.mantissa);
    if (!Number.isFinite(mantissa)) return;
    const value = mantissa * scaleFactor(current.scale);
    onCommit(Math.min(Math.max(value, min), max ?? Number.MAX_SAFE_INTEGER));
  };

  return (
    <span
      className="number-field editing"
      // Only commit once focus has left the field entirely — moving between the
      // input and the scale dropdown must not close the editor.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          setDraft(null);
        }
      }}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        aria-label={label}
        value={draft.mantissa}
        onChange={(event) => setDraft({ ...draft, mantissa: event.target.value })}
      />
      {suffix ? <span className="suffix">{suffix}</span> : null}
      {scalable ? (
        <select
          className="scale-select"
          aria-label={`${label} scale`}
          value={draft.scale}
          onChange={(event) => setDraft({ ...draft, scale: event.target.value as ScaleKey })}
        >
          {SCALES.map((scale) => (
            <option key={scale.key} value={scale.key}>
              {scale.label}
            </option>
          ))}
        </select>
      ) : null}
    </span>
  );
}

export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'accent' | 'warn' | 'muted';
}) {
  return (
    <div className={`stat${tone ? ` stat-${tone}` : ''}`}>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {sub ? <span className="stat-sub">{sub}</span> : null}
    </div>
  );
}

/** "a ⟶ b (+c)" — the from/to readout used everywhere in the app. */
export function Transition({
  from,
  to,
  delta,
}: {
  from: ReactNode;
  to: ReactNode;
  delta?: ReactNode;
}) {
  return (
    <span className="transition">
      <b>{from}</b>
      <span className="arrow">⟶</span>
      <b>{to}</b>
      {delta !== undefined ? <span className="delta">({delta})</span> : null}
    </span>
  );
}
